import type { FeedbackItem } from '../types';
import { computePinPoint } from './pinPosition';
import { getMarkupWindow } from './deviceFrame';

/**
 * Opening a ticket that lives on another page means leaving the current one, so
 * the "which ticket was I opening?" intent has to survive the navigation. It's
 * kept in sessionStorage: it belongs to this tab, and it should not outlive the
 * browsing session.
 */
const PENDING_KEY = 'markup:pending-focus';

/**
 * A hand-off older than this is stale — the user landed somewhere else, or the
 * navigation never completed and they've moved on. Without a cut-off, a failed
 * jump would re-open the ticket on some unrelated later visit.
 */
const PENDING_TTL_MS = 2 * 60 * 1000;

export interface PendingFocus {
  /** Feedback id to select once the target page is up. */
  id: string;
  /** Page the ticket was raised on, used to confirm we've actually arrived. */
  url: string;
  savedAt: number;
}

export function setPendingFocus(id: string, url: string): void {
  try {
    sessionStorage.setItem(
      PENDING_KEY,
      JSON.stringify({ id, url, savedAt: Date.now() } satisfies PendingFocus)
    );
  } catch (_) {
    // Private mode / storage disabled — the jump still navigates, it just
    // won't re-open the ticket on arrival.
  }
}

export function readPendingFocus(): PendingFocus | null {
  try {
    const raw = sessionStorage.getItem(PENDING_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as Partial<PendingFocus>;
    if (!parsed?.id || !parsed?.url || typeof parsed.savedAt !== 'number') {
      clearPendingFocus();
      return null;
    }

    if (Date.now() - parsed.savedAt > PENDING_TTL_MS) {
      clearPendingFocus();
      return null;
    }

    return parsed as PendingFocus;
  } catch (_) {
    return null;
  }
}

export function clearPendingFocus(): void {
  try {
    sessionStorage.removeItem(PENDING_KEY);
  } catch (_) {
    /* nothing to clear */
  }
}

/**
 * Where to open a ticket's page on the site currently being browsed, or null
 * when there's nowhere here to go.
 *
 * Tickets store the full href they were raised on, which may be another copy
 * of the site — a markup made on staging, opened while developing on
 * localhost. Following that href would take the reviewer off their own
 * environment. Under `pathname` matching it's the same page, so it opens at
 * the same path here instead. Under `exact` matching another origin is
 * another page, so the reviewer stays put.
 */
export function pageUrlHere(
  url: string,
  mode: 'pathname' | 'exact' = 'pathname'
): string | null {
  try {
    const target = new URL(url, window.location.href);
    if (target.origin === window.location.origin) return target.href;
    if (mode === 'exact') return null;
    return new URL(
      target.pathname + target.search + target.hash,
      window.location.origin
    ).href;
  } catch (_) {
    return null;
  }
}

/**
 * Centre the viewport on a ticket's pin — the device preview's viewport while
 * one is open. No-op for tickets without one.
 */
export function scrollToPin(
  feedback: FeedbackItem,
  behavior: ScrollBehavior = 'smooth'
): void {
  if (!feedback.pinLocation) return;

  const view = getMarkupWindow();
  const { left, top } = computePinPoint(feedback.pinLocation, view);
  view.scrollTo({
    left: Math.max(0, left - view.innerWidth / 2),
    top: Math.max(0, top - view.innerHeight / 2),
    behavior,
  });
}

/**
 * Scroll to a pin on a page that has only just loaded.
 *
 * Images and webfonts land after first paint and push content down, so a single
 * scroll on arrival routinely stops short of the pin. Re-running it as the
 * layout settles lands on the right spot. `auto` rather than `smooth` because
 * overlapping smooth scrolls fight each other.
 *
 * Returns a cancel function for effect cleanup.
 */
export function scrollToPinWhenSettled(
  feedback: FeedbackItem,
  delays: number[] = [0, 250, 800]
): () => void {
  const timers = delays.map((delay) =>
    window.setTimeout(() => scrollToPin(feedback, 'auto'), delay)
  );

  return () => timers.forEach((timer) => window.clearTimeout(timer));
}
